import './styles.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { msalInstance } from './auth/msal.js';
import { App } from './App.js';
import { RootErrorBoundary } from './app-kit/error-boundary.js';
import { installConsoleCapture, startStreaming } from './observability/console-capture.js';
import { reportNonHttpError } from './observability/report-non-http-error.js';

// Browser-side capture: console ring buffer + window.onerror + onunhandledrejection.
// Auto-bug-reports always fire on uncaught errors. In dev we additionally
// stream every console.* line to the api so they show up in the dev stdout
// Claude tails via Monitor — DEV_AUTH_BYPASS=true makes the SPA an Admin
// from boot, so the system:observe gate on /api/system/client-events lets
// the requests through. Prod gates streaming on real sign-in (wired in
// AppShell once the user is known).
installConsoleCapture();
if (import.meta.env.DEV) startStreaming();

try {
  await msalInstance.initialize();
} catch (err) {
  // Don't block app render in dev if MSAL config is incomplete.
  console.warn('[msal] init failed; sign-in unavailable. Use dev bypass.', err);
}

// Trigger #2 of the bug-report flow: api call failures from the SPA become
// auto-bug-reports. The `meta.silent` query option lets a caller opt out for
// noisy/expected errors (e.g. polling endpoints during a deploy).
//
// ⚠️ THE REPORT ITSELF NO LONGER HAPPENS HERE. `apiJson` files it at the
// network layer (`app-kit/request-service.ts`), because a hook on the LIBRARY
// only ever saw failures that went through a query or a mutation — and the
// reports page awaits `apiJson` directly inside its own try/catch, so every
// Run and every Download was invisible. A live 500 on the 12 Month Analysis
// report produced zero bug reports across three days on preview for exactly
// that reason.
//
// So this hook narrows to what only it can see: an error a `queryFn` threw
// that never was an http failure. It no longer reports `HttpError`, because
// `apiJson` already did — and the two would not de-duplicate, since the dedupe
// key is `source:message` and the messages differ (`query foo: …` vs
// `500 /api/x: …`).
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (query.meta?.silent) return;
      reportNonHttpError(err, `query ${String(query.queryKey[0] ?? 'unknown')}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      reportNonHttpError(err, `mutation ${mutation.options.mutationKey?.[0] ?? 'unknown'}`);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,         // 30s — cache freshness for dashboard data
      retry: 1,                   // one retry; don't hammer broken endpoints
      refetchOnWindowFocus: false, // most admin data isn't second-by-second critical
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </MsalProvider>
    </RootErrorBoundary>
  </StrictMode>,
);
